import { ColoursObj, Typography } from '@eventr/common-storybook'
import { Stack } from '@mui/material'
import Markdown, { Components } from 'react-markdown'
import remarkGemoji from 'remark-gemoji'
import remarkGfm from 'remark-gfm'
import remarkSmartypants from 'remark-smartypants'
import { openBrowser } from '../../../utils'
import { ErrorBoundary } from '@sentry/react'
import rehypeRaw from 'rehype-raw'

interface MarkdownRowProps {
    value: string
    past?: boolean
}

const markdownComponents = (
    extraProps: { past?: boolean } = {}
): Components => {
    const { past } = extraProps
    const colour = past ? ColoursObj.shades.shade2 : ColoursObj.font.primary
    const themeProps = { color: colour }

    return {
        h1: ({ node, ...props }: { node?: unknown; [key: string]: any }) => (
            <Typography {...themeProps} variant="mobileH1" {...props} />
        ),
        h2: ({ node, ...props }: { node?: unknown; [key: string]: any }) => (
            <Typography {...themeProps} variant="mobileH2" {...props} />
        ),
        h3: ({ node, ...props }: { node?: unknown; [key: string]: any }) => (
            <Typography {...themeProps} variant="mobileH3" {...props} />
        ),
        p: ({ node, ...props }: { node?: unknown; [key: string]: any }) => (
            <Typography {...themeProps} variant="bodyRegular" {...props} />
        ),
        li: ({ node, ...props }: { node?: unknown; [key: string]: any }) => (
            <Typography
                {...themeProps}
                component="li"
                variant="bodyRegular"
                sx={{ color: ColoursObj.font.primary }}
                {...props}
            />
        ),
        ul: ({ node, ...props }: { node?: unknown; [key: string]: any }) => (
            <Typography
                {...themeProps}
                component="ul"
                variant="bodyRegular"
                sx={{ color: ColoursObj.font.primary, paddingLeft: '1.5rem' }}
                {...props}
            />
        ),
        strong: ({
            node,
            ...props
        }: {
            node?: unknown
            [key: string]: any
        }) => <Typography {...themeProps} variant="bodyBold" {...props} />,
        em: ({ node, ...props }: { node?: unknown; [key: string]: any }) => (
            <Typography
                {...themeProps}
                variant="bodyRegular"
                sx={{ fontStyle: 'italic' }}
                {...props}
            />
        ),
        del: ({ node, ...props }: { node?: unknown; [key: string]: any }) => (
            <Typography
                {...themeProps}
                variant="bodyRegularStrikethrough"
                {...props}
            />
        ),
        a: ({ node, ...props }: { node?: unknown; [key: string]: any }) => (
            <Typography
                {...themeProps}
                variant="bodyRegularUnderlined"
                sx={{ color: ColoursObj.core.secondary }}
                onClick={
                    props.onClick
                        ? () => props.onClick(props.href)
                        : () => openBrowser(props.href)
                }
                {...props}
            />
        ),
        hr: ({ node, ...props }: { node?: unknown; [key: string]: any }) => (
            <hr
                style={{
                    border: 'none',
                    borderTop: `1px solid ${ColoursObj.font.primary}`,
                    margin: '1rem 0',
                }}
                {...props}
            />
        ),
        pre: ({ node, ...props }: { node?: unknown; [key: string]: any }) => (
            <Typography
                variant="bodyRegular"
                sx={{
                    backgroundColor: ColoursObj.backgrounds.primary,
                    padding: '1rem',
                    borderRadius: '0.5rem',
                    overflowX: 'auto',
                }}
                {...props}
            />
        ),

        code: ({ node, ...props }: { node?: unknown; [key: string]: any }) => (
            <Typography
                variant="bodyRegular"
                sx={{
                    backgroundColor: ColoursObj.backgrounds.primary,
                    padding: '0.25rem 0.5rem',
                    borderRadius: '0.25rem',
                    fontFamily: 'monospace',
                }}
                {...props}
            />
        ), // not dealing with syntax highlighting for now
        table: ({ node, ...props }: { node?: unknown; [key: string]: any }) => (
            <table
                style={{
                    borderCollapse: 'collapse',
                    width: '100%',
                    margin: '1rem 0',
                }}
                {...props}
            />
        ),
        th: ({ node, ...props }: { node?: unknown; [key: string]: any }) => (
            <Typography
                component="th"
                variant="bodyBold" // Use header 3 variant for table headers
                sx={{
                    border: `1px solid ${ColoursObj.font.primary}`,
                    padding: '0.5rem',
                    textAlign: 'left',
                    backgroundColor: ColoursObj.backgrounds.secondary,
                }}
                {...props}
            />
        ),
        // br: ({ node, ...props }: { node?: unknown; [key: string]: any }) => (
        //     <br

        //     />
        // ),
        td: ({ node, ...props }: { node?: unknown; [key: string]: any }) => (
            <Typography
                component="td"
                variant="bodyRegular"
                sx={{
                    border: `1px solid ${ColoursObj.font.primary}`,
                    padding: '0.5rem',
                }}
                {...props}
            />
        ),
        blockquote: ({
            node,
            ...props
        }: {
            node?: unknown
            [key: string]: any
        }) => (
            <Typography
                variant="bodyRegular"
                sx={{
                    borderLeft: `4px solid ${ColoursObj.font.primary}`,
                    paddingLeft: '1rem',
                    margin: '1rem 0',
                    fontStyle: 'italic',
                }}
                {...props}
            />
        ),
        img: ({ node, ...props }: { node?: unknown; [key: string]: any }) => (
            <img
                style={{
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '0.5rem',
                }}
                alt={props.alt || ''}
                {...props}
            />
        ),
        sub: ({ node, ...props }: { node?: unknown; [key: string]: any }) => (
            <Typography
                variant="bodyRegular"
                sx={{ fontSize: '0.8em', verticalAlign: 'sub' }}
                {...props}
            />
        ),

        sup: ({ node, ...props }: { node?: unknown; [key: string]: any }) => (
            <Typography
                variant="bodyRegular"
                sx={{ fontSize: '0.8em', verticalAlign: 'super' }}
                {...props}
            />
        ),
    }
}
function preserveEmptyLines(md: string) {
    let regex = /\n/
    // Replace two or more consecutive newlines with \n<br/>\n for each extra empty line
    return md?.replaceAll(new RegExp(regex, 'g'), '<br /> ') ?? ''
}
export const MarkdownRow = ({ value, past }: MarkdownRowProps) => {
    if (!value) return null
    const processedValue = preserveEmptyLines(value)

    return (
        <Stack>
            <ErrorBoundary fallback={<Typography>{value}</Typography>}>
                <Markdown
                    components={markdownComponents({ past })}
                    children={processedValue}
                    rehypePlugins={[rehypeRaw]} // Enable rehype plugins
                    remarkPlugins={[remarkGfm, remarkSmartypants, remarkGemoji]} // Enable plugins
                />
            </ErrorBoundary>
        </Stack>
    )
}
